/**
 * Rolling-window (1h/5h/24h/7d/30d/lifetime) token-usage-per-task panel — the visual
 * counterpart to GET /api/backlog/usage/windows (2026-08-05 operator ask: "tokens
 * per task, over time, broken down by category, vs number of tasks completed").
 * Unlike DeepSeekUsageTable (layout.tsx, per-ACCOUNT, DeepSeek-only, in/out only),
 * this is per-TASK, any provider, full four-category breakdown, plus task_count and
 * the derived per-task averages that actually answer "where is spend concentrated."
 *
 * Self-contained fetch + poll, same shape as VmResources.tsx / FleetGit.tsx: pure
 * exported formatters here (vitest-covered in TaskUsageWindows.test.ts), the React
 * shell only owns the fetch/poll/role-group/account-filter state — the provider/
 * model filter itself is shared with BatchingEfficiencyPanel via UsageFilterStore.ts
 * (2026-08-19 operator report: clicking a provider here left that panel unchanged).
 */

import { lazy, Suspense, useEffect, useState } from "react";

import { api } from "./api";
import { Panel, fmtTokenCount } from "./components";
import type { AccountView, AgentView, ModelDeathRatesView, TaskUsageWindowView, TopTaskCostView } from "./types";
import { useSharedProviderFilterIndex } from "./UsageFilterStore";

// Lazy: recharts (pulled in by UsageTimeSeriesModal) only loads once the operator
// actually opens the usage-over-time modal — see
// ao_control_plane_read_models_and_request_path_2026_09_04.md [UI] P0.
const UsageTimeSeriesModal = lazy(() =>
  import("./UsageTimeSeriesModal").then((m) => ({ default: m.UsageTimeSeriesModal })),
);

const POLL_INTERVAL_MS = 60000;

const _WINDOW_LABELS: Record<string, string> = {
  "1h": "1h",
  "5h": "5h",
  "24h": "24h",
  "7d": "7d",
  "30d": "30d",
  lifetime: "Lifetime",
};
const _WINDOW_ORDER = ["1h", "5h", "24h", "7d", "30d", "lifetime"];

/** Each option scopes `provider` and, optionally, an exact `model` — the
 * deepseek-v4-pro/-flash entries let the pro-vs-flash A/B test
 * (deepseek_flash_ab_routing_test_2026_08_05) compare the two variants
 * individually, while plain "DeepSeek" still shows them combined.
 *
 * One button per value of the backend's `AccountProvider` Literal
 * (server/accounts.py) that has ever had a registered account — 2026-08-19
 * operator report: five live providers (Gemini, GLM, Codex/Luna, Kimi [since
 * removed, see below], Gemma/NVIDIA) had real accounts in the registry but no
 * provider-level filter button, only reachable via the per-account toggle
 * below. openrouter/groq/
 * sambanova are valid AccountProvider values with no registered
 * account today — omitted rather than adding a permanently-empty button; add
 * one here if/when an account is actually registered under it. (Grok/xAI had
 * a button here too until it was fully decommissioned 2026-08-18 and fully
 * removed 2026-08-20 per operator ruling — no accounts, no Literal value, no
 * button. Kimi/OmniRoute/OpenRouter similarly fully removed 2026-08-21 per
 * operator ruling — unused code debt, no accounts, no Literal value where
 * applicable, no button.)
 *
 * `ollama` added 2026-08-20 (kimi_gemma_provider_onboarding_2026_08_16): the
 * self-hosted Gemma 3 27B account (`gemma-self-hosted`) supersedes the two
 * `nvidia`-hosted Gemma accounts, which are now deregistered — that button is
 * kept (not deleted) specifically so an operator can still tell the two Gemma
 * paths apart in this filter and in historical task-usage data, rather than
 * having the old NVIDIA-era rows silently vanish from the picker. */
export const PROVIDER_MODEL_FILTER_OPTIONS: {
  provider: string | undefined;
  model: string | undefined;
  label: string;
}[] = [
  { provider: undefined, model: undefined, label: "All providers" },
  { provider: "deepseek", model: undefined, label: "DeepSeek (all)" },
  { provider: "deepseek", model: "deepseek-v4-pro", label: "DeepSeek · Pro" },
  { provider: "deepseek", model: "deepseek-v4-flash", label: "DeepSeek · Flash" },
  { provider: "anthropic", model: undefined, label: "Anthropic" },
  { provider: "gemini", model: undefined, label: "Gemini" },
  { provider: "glm", model: undefined, label: "GLM" },
  { provider: "codex", model: undefined, label: "Codex/Luna" },
  { provider: "nvidia", model: undefined, label: "Gemma (NVIDIA, deregistered)" },
  { provider: "ollama", model: undefined, label: "Gemma (self-hosted)" },
  // muse_spark_provider_addition_2026_08_25: direct account registration, same
  // plain-provider pattern as codex/gemini above (no per-tier split — only the
  // contributor tier is ever registered).
  { provider: "muse", model: undefined, label: "Muse" },
  // Rows with NO provider attribution — server sentinel, matches provider IS NULL
  // (2026-08-24 operator ask). The largest daily bucket was provider-NULL (317
  // tasks / 21,215 turns measured on 2026-08-23) and invisible in every breakdown;
  // a first-class chip keeps the attribution gap measurable until the backfill
  // closes it, and keeps proving it stays closed afterwards.
  { provider: "unattributed", model: undefined, label: "Unattributed" },
];

/** Second, independent filter axis (ao_task_usage_role_group_breakdown_2026_08_06,
 * operator ask: split cicd/scheduled/planning work apart, with conflict_resolver and
 * data_pipeline_failure kept individually filterable rather than folded into "cicd" —
 * see server/state_store/slots.py's task_role_group() for the collapse rule this
 * mirrors). Composes AND-wise with PROVIDER_MODEL_FILTER_OPTIONS (independent state, same as
 * provider+model already compose server-side). */
export const ROLE_GROUP_FILTER_OPTIONS: { roleGroup: string | undefined; label: string }[] = [
  { roleGroup: undefined, label: "All roles" },
  { roleGroup: "planning", label: "Planning" },
  { roleGroup: "scheduled", label: "Scheduled" },
  { roleGroup: "cicd", label: "CI/CD" },
  { roleGroup: "conflict_resolver", label: "Conflict resolver" },
  { roleGroup: "data_pipeline_failure", label: "Data pipeline failure" },
  { roleGroup: "quality_gate_resolution", label: "Quality gate resolution" },
  { roleGroup: "human", label: "Human" },
  { roleGroup: "planning-human", label: "Human (planning)" },
  // task_usage_cost_tracking_gaps_2026_08_27 [BACKEND] P2: main(slot 0)/review's own
  // periodic self-usage-capture rows (main_review_usage_capture.py) — previously
  // invisible to every reconciliation and this panel entirely. "review_agent",
  // deliberately not bare "review" — see server/state_store/slots.py's
  // _AGENT_SELF_USAGE_ROLES comment for why.
  { roleGroup: "main_agent", label: "Main agent" },
  { roleGroup: "review_agent", label: "Review agent" },
];

// ── pure display mappers (vitest-covered) ──────────────────────────────────

/** Third, independent filter axis (anthropic_per_task_actual_spend_and_account_
 * calibration_2026_08_10): scope cost to one subscription. Built from the live
 * accounts registry rather than a hardcoded list — accounts are added, paused and
 * rotated often enough that a literal here would be stale within days, and a filter
 * button for an account that no longer exists silently shows an empty panel.
 *
 * Sorted by account_id so the button order is stable across polls (the registry's own
 * order is not guaranteed, and buttons that reshuffle under the cursor are a real
 * misclick hazard on a panel the operator uses to compare slices).
 *
 * `provider`, when given, scopes the list to only that provider's accounts (2026-08-19
 * operator report: selecting "DeepSeek" still left every Anthropic/Gemini/… account
 * button in the row, forcing a scroll to find the relevant ones). Omit it (or pass
 * undefined, matching PROVIDER_MODEL_FILTER_OPTIONS' "All providers" entry) to keep the
 * full, unscoped registry. */
export function accountFilterOptions(
  accounts: AccountView[],
  provider?: string,
): { accountId: string | undefined; label: string }[] {
  const relevant = provider === undefined ? accounts : accounts.filter((a) => a.provider === provider);
  const sorted = [...relevant].sort((a, b) => a.account_id.localeCompare(b.account_id));
  return [
    { accountId: undefined, label: "All accounts" },
    ...sorted.map((a) => ({ accountId: a.account_id, label: a.label || a.account_id })),
  ];
}

/** Structurally typed on `{window}` alone (not TaskUsageWindowView-specific) so
 * BatchingEfficiencyPanel.tsx's BatchingStatsWindowView reuses this instead of a
 * near-identical copy — same "structural, not feature-specific" reasoning already
 * applied to totalTokens() below. */
export function sortWindows<T extends { window: string }>(windows: T[]): T[] {
  const rank = new Map(_WINDOW_ORDER.map((w, i) => [w, i]));
  return [...windows].sort((a, b) => (rank.get(a.window) ?? 99) - (rank.get(b.window) ?? 99));
}

export function windowLabel(window: string): string {
  return _WINDOW_LABELS[window] ?? window;
}

/** "$0.0271" — "—" for null (unpriced; never a misleading $0.00). */
export function fmtUsdOrDash(amount: number | null): string {
  if (amount === null) return "—";
  return `$${amount.toFixed(4)}`;
}

/** Explains a null spend cell instead of leaving a bare dash that reads as broken
 * (operator report: "billing breakdown ... looking pretty stuck"). One unpriced task
 * nulls the whole window by design — see TaskUsageWindowTotals' "never a partial/
 * misleading sum" rule. A real amount, or a priced-but-zero window
 * (unpriced_row_count === 0), gets no title at all.
 *
 * The wording deliberately no longer blames "Anthropic/Claude": that WAS the dominant
 * cause while the rate table was DeepSeek-only, but Anthropic models are registered as
 * of 2026-08-10, so naming them here would now send the reader after the wrong thing.
 * A dash today means an unregistered model or a row awaiting repricing. */
export function spendCellTitle(amount: number | null, unpricedRowCount: number): string | undefined {
  if (amount !== null || unpricedRowCount <= 0) return undefined;
  const noun = unpricedRowCount === 1 ? "task" : "tasks";
  return (
    `${unpricedRowCount} ${noun} in this window ran on a model with no registered $ price, or predate ` +
    `repricing — spend is intentionally left blank rather than an understated partial sum.`
  );
}

/** Explains a priced-but-estimated spend cell — a window where 1+ rows' spend/cache
 * numbers are the 98%-cache-fraction APPROXIMATION, not real per-turn ledger data
 * (codex_historical_cache_estimate_2026_08_24 — see TaskUsageRow.pricing_is_estimated's
 * own docstring for why: a pre-ledger codex session with no real cache breakdown to
 * reprice from). Composes with spendCellTitle rather than conflicting with it: a fully
 * unpriced window (amount === null) never reaches here since spendCellTitle already
 * explains that case; this only fires once the cell has a real, non-null number to
 * annotate as rough. */
export function estimatedCellTitle(amount: number | null, estimatedRowCount: number): string | undefined {
  if (amount === null || estimatedRowCount <= 0) return undefined;
  const noun = estimatedRowCount === 1 ? "task" : "tasks";
  return (
    `${estimatedRowCount} ${noun} in this window predate the Codex per-turn ledger (2026-08-24) and have no real ` +
    `cache-read breakdown to price from — approximated at 98% cache-read / 2% fresh input (operator rule of ` +
    `thumb), not real per-turn billing data.`
  );
}

/** A window's spend as a priced lower bound: "at least $X, N unmeasured[, M unpriced]". Returns undefined
 * when the window has nothing unmeasured and nothing unpriced (the exact `spend_usd` cell applies) or no
 * priced figure exists to bound from. Unmeasured rows are node tombstones (spend we could not measure);
 * unpriced rows ran on a model with no registered price. */
export function spendLowerBoundText(
  spendUsd: number | null,
  pricedSpendUsd: number | null | undefined,
  unpricedRowCount: number,
  unmeasuredRowCount: number | undefined,
): string | undefined {
  const unmeasured = unmeasuredRowCount ?? 0;
  if (unmeasured <= 0 && (spendUsd !== null || unpricedRowCount <= 0)) return undefined;
  const amount = pricedSpendUsd ?? spendUsd;
  if (amount === null || amount === undefined) return undefined;
  const parts = [`at least ${fmtUsdOrDash(amount)}`];
  if (unmeasured > 0) parts.push(`${unmeasured} unmeasured`);
  if (spendUsd === null && unpricedRowCount > 0) parts.push(`${unpricedRowCount} unpriced`);
  return parts.join(", ");
}

/** A per-unit spend ($/task, $/turn) as a lower bound: "≥$0.0271". The exact figure is null only because some
 * rows in the window are unpriced (one unpriced row blanks the whole window by design), which used to leave
 * the whole column as "—". The priced rows' spend divided by ALL tasks (or turns) is a true lower bound of the
 * exact figure, so it can be shown without ever overstating it. undefined when the exact figure exists, when
 * nothing is unpriced, or when there is no priced figure or denominator to bound from. */
export function perUnitLowerBoundText(
  exact: number | null,
  pricedSpendUsd: number | null | undefined,
  unpricedRowCount: number,
  denominator: number,
): string | undefined {
  if (exact !== null || unpricedRowCount <= 0) return undefined;
  if (pricedSpendUsd === null || pricedSpendUsd === undefined || denominator <= 0) return undefined;
  return `≥${fmtUsdOrDash(pricedSpendUsd / denominator)}`;
}

/** Tooltip for a perUnitLowerBoundText cell. */
export function perUnitLowerBoundTitle(unit: "task" | "turn", unpricedRowCount: number): string {
  return (
    `Lower bound: the priced rows' spend divided by every ${unit} in the window. ${unpricedRowCount} unpriced ` +
    `row(s) (a model with no registered price, or awaiting repricing) are not in the numerator.`
  );
}

/** "~$0.0271" vs "$0.0271" — the "~" prefix flags a window with 1+ estimated rows (see
 * estimatedCellTitle). A null amount or an all-real window renders via plain
 * fmtUsdOrDash, unprefixed. */
export function fmtUsdOrDashWithEstimateFlag(amount: number | null, estimatedRowCount: number): string {
  const base = fmtUsdOrDash(amount);
  if (amount === null || estimatedRowCount <= 0) return base;
  return `~${base}`;
}

/** "57.3" — "—" for null (no tasks/turns to divide by). Turn/task ratios stay in the
 * 1-2,000 range this fleet actually sees, so a plain fixed-point number reads better
 * than fmtTokenCount's K/M abbreviation (built for raw token counts, not ratios). */
export function fmtRatioOrDash(n: number | null): string {
  if (n === null) return "—";
  return n.toFixed(1);
}

/** Rounds before delegating to fmtTokenCount — per-turn token AVERAGES are fractional
 * (e.g. 16043.7), and fmtTokenCount's own <1000 branch does a bare String(n), which
 * would render every trailing decimal for a small fractional average. */
export function fmtTokenAvgOrDash(n: number | null): string {
  if (n === null) return "—";
  return fmtTokenCount(Math.round(n));
}

/** "—" for null — the reasoning_tokens column's null-vs-zero contract
 * (deepseek_native_usage_reasoning_tokens_visibility_2026_08_12): null means "no
 * DeepSeekNativeUsageRow matched any task in this window" (non-DeepSeek provider
 * filter, or DeepSeek tasks that predate the native proxy) — a dash, never "0",
 * since 0 would misreport "measured zero" instead of "not captured". */
export function fmtTokenCountOrDash(n: number | null): string {
  if (n === null) return "—";
  return fmtTokenCount(n);
}

/** "85.3 t/s" — "—" for null (no timed rows to divide by). Comparability label:
 * Z.ai's own public status-page "Peak avg Decode" (Max&Pro ~85-120 t/s, Lite
 * ~65-95 t/s observed 2026-08-21..27, per-instance vs account-shared unknown —
 * cited for reference only, NOT a cap threshold — this plan explicitly defers
 * any threshold decision until real data accumulates). */
export function fmtTokensPerSecOrDash(n: number | null): string {
  if (n === null) return "—";
  return `${n.toFixed(1)} t/s`;
}

/** Total across all four categories — the same "raw token count" the operator's
 * original 2.5B-token question was about. Structurally typed (not TaskUsageWindowView-
 * specific) so the per-task detail view (TaskUsageView, App.tsx's BacklogDetailModal)
 * reuses this instead of re-summing the same four fields a second way. */
export function totalTokens(w: {
  input_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  output_tokens: number;
}): number {
  return w.input_tokens + w.cache_creation_input_tokens + w.cache_read_input_tokens + w.output_tokens;
}

/** Filter-query value for one registered human host's own usage slice
 * (ao_v2_node_and_human_slot_recognition_2026_09_04 H6 — "human:<host>", e.g.
 * "human:ikenna"), matching the backend's own role-group convention once
 * human_usage_push starts carrying a host_id (the sibling H6 BACKEND todos in the
 * same plan). Rendered verbatim as the row label too — "render exactly what the API
 * returns" per the ui_developer craft rule, no separate display-name mapping. */
export function humanHostRoleGroup(hostLabel: string): string {
  return `human:${hostLabel}`;
}

/** Dedupe + sort registered human/planning-human agent labels for a stable,
 * reshuffle-free row order across polls — same reasoning as accountFilterOptions'
 * account_id sort above. */
export function humanHostLabels(agents: Pick<AgentView, "label">[]): string[] {
  return [...new Set(agents.map((a) => a.label))].sort();
}

// ── component ────────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// Per-task cost RANKING formatters (anthropic_per_task_actual_spend_and_account_
// calibration_2026_08_10 [BACKEND] P2) — pure, so they are vitest-covered directly
// rather than only through the polling shell.
// ---------------------------------------------------------------------------

/** Human label for a provider's PUBLISHED billing shape
 * (server/model_pricing.py::billing_shape_for_provider — deliberately NOT a private
 * vocabulary: the three subscription shapes differ in HOW they are priced, but they
 * share the one property this panel needs, which is that the list value is not cash. */
export function spendBasisLabel(basis: string | null): string {
  switch (basis) {
    case "metered_dollar":
      return "metered";
    case "subscription_boost_multiplier":
    case "subscription_credits":
    case "subscription_unknown":
      return "subscription";
    case "rate_limited_free":
      return "free tier";
    default:
      return "unrated";
  }
}

/** Inline basis badge for a Spend cell — appended INSIDE the existing `<td>`, never
 * as its own column, so the column-position `nth()` index every sibling e2e spec
 * asserts against (task-usage-account-filter.spec.ts etc.) never shifts. Renders
 * nothing for a null basis (unscoped/mixed window) or "metered" (DeepSeek's real
 * cash figure needs no caveat) — only the subscription/free-tier/unrated cases need
 * a reader to know "this $ figure is not cash" at a glance
 * (anthropic_per_task_actual_spend_and_account_calibration_2026_08_10 [UI] P2). */
export function SpendBasisBadge({ basis }: { basis: string | null }) {
  if (basis === null || basis === "metered_dollar") return null;
  return (
    <span
      className="spend-basis-badge"
      title="List-priced value, not cash — this provider runs on a flat-rate subscription (or free/uncategorised tier); real cost needs a measured value multiplier."
    >
      {" "}
      ({spendBasisLabel(basis)})
    </span>
  );
}

/** Lookback hours for a window label; null for the lifetime (no-cutoff) window — which
 * is also the wire encoding the server expects for "no cutoff" (window_hours <= 0). */
export function windowLookbackHours(label: string): number | null {
  switch (label) {
    case "1h":
      return 1;
    case "5h":
      return 5;
    case "24h":
      return 24;
    case "7d":
      return 24 * 7;
    case "30d":
      return 24 * 30;
    default:
      return null;
  }
}

/** Tooltip for a ranked row's dollar: says plainly whether it is cash, and why not.
 * Returns undefined for a row that needs no caveat (real metered spend). */
export function costRankingCellTitle(
  row: Pick<TopTaskCostView, "absolute_is_provisional" | "spend_basis" | "unpriced_row_count" | "list_value_usd">,
): string | undefined {
  if (row.list_value_usd === null) {
    return `No total shown: ${row.unpriced_row_count} attempt row(s) have no registered price. A partial sum would understate this task AND silently reorder the ranking, so it is left blank instead.`;
  }
  if (!row.absolute_is_provisional) {
    return undefined;
  }
  switch (row.spend_basis) {
    case "subscription_boost_multiplier":
      return "List-priced value, NOT cash: this runs on a flat-rate subscription, so real cost is list value divided by that account's measured value multiplier. The RANKING is unaffected — the same multiplier divides every task in the window.";
    case "subscription_credits":
      return "List-priced value, NOT cash: this runs on a flat-rate credit subscription, so the real cost needs the plan's measured multiplier. The ordering is unaffected.";
    case "rate_limited_free":
      return "Not billed per token: this provider runs on a rate-limited free tier, so this is what the tokens WOULD have cost on a metered API, not what we paid.";
    default:
      return "Provisional: this provider's billing shape is not categorised yet, so the figure is list-priced value and may not be cash.";
  }
}

/** "12.3" for a per-100 rate, "—" when there is nothing to divide by.
 * A rate with a zero denominator is NOT 0% — it is unknown, and rendering it as 0
 * would read as "this model never dies", exactly the undercount this panel exists to
 * expose. Exported for vitest (the file's pure-formatter convention). */
export function fmtPer100(rate: number, denominator: number): string {
  if (denominator <= 0) return "—";
  return rate.toFixed(rate >= 10 ? 1 : 2);
}

export function TaskUsageWindowsPanel({ baseUrl }: { baseUrl: string }) {
  const [windows, setWindows] = useState<TaskUsageWindowView[]>([]);
  const [filterIndex, setFilterIndex] = useSharedProviderFilterIndex();
  const [roleGroupIndex, setRoleGroupIndex] = useState(0);
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [accountIndex, setAccountIndex] = useState(0);
  // task_usage_cost_tracking_gaps_2026_08_27 [UI] P1: composes AND-wise with every
  // other filter row (server/state_store/slots.py's window_task_usage_totals own
  // exclude_died_mid_task param) — off by default, since a died-mid-task row is real
  // captured spend that stays included in every total unless this is explicitly on.
  const [excludeDiedMidTask, setExcludeDiedMidTask] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [usageTsOpen, setUsageTsOpen] = useState(false);
  const filter = PROVIDER_MODEL_FILTER_OPTIONS[filterIndex] ?? PROVIDER_MODEL_FILTER_OPTIONS[0];
  const roleGroupFilter = ROLE_GROUP_FILTER_OPTIONS[roleGroupIndex] ?? ROLE_GROUP_FILTER_OPTIONS[0];
  // cost_ranked_model_routing_rebuild_2026_08_28 — restore model dimension to
  // token/spend breakdowns. Multipliers stay keyed by (provider, plan,
  // date-window) and never go per-model; this breakdown covers token/spend only
  // and lives entirely client-side by re-querying the existing
  // /api/backlog/usage/windows endpoint per model slice — no multiplier logic
  // touched, no new bucketing.
  const [modelBreakdown, setModelBreakdown] = useState<{ label: string; windows: TaskUsageWindowView[] }[]>([]);
  const [modelBreakdownError, setModelBreakdownError] = useState<string | null>(null);
  // ao_v2_node_and_human_slot_recognition_2026_09_04 H6(display): per-host totals,
  // one row per registered human/planning-human laptop, shown only when the "Human"
  // role-group filter is active — analogous to modelBreakdown above but keyed on
  // role_group ("human:<host>") instead of provider.
  const [humanHostBreakdown, setHumanHostBreakdown] = useState<{ label: string; windows: TaskUsageWindowView[] }[]>([]);
  const [humanHostBreakdownError, setHumanHostBreakdownError] = useState<string | null>(null);
  // Per-model death rates (ao_death_reasons_gate_starvation_and_cost_columns_findings_
  // 2026_10_02 [BACKEND] P2): the transcript died_mid_task rate and the activity log's
  // work-losing tmux_session_lost rate, side by side — the flag alone undercounts by an
  // order of magnitude (GLM measured 0.6% flagged vs 14.3 per 100 tasks). Its own window
  // selector, because a death rate is only meaningful over a stated period and the
  // window table above has no single window selected (it renders all of them).
  const [deathRates, setDeathRates] = useState<ModelDeathRatesView | null>(null);
  const [deathRatesError, setDeathRatesError] = useState<string | null>(null);
  const [deathRatesWindow, setDeathRatesWindow] = useState("7d");
  // Scoped to the shared provider filter so switching provider drops any account
  // button that no longer applies, instead of leaving the full registry to scroll
  // through (2026-08-19 operator report).
  const accountOptions = accountFilterOptions(accounts, filter.provider);
  const accountFilter = accountOptions[accountIndex] ?? accountOptions[0];
  // anthropic_per_task_actual_spend_and_account_calibration_2026_08_10 [UI] P2:
  // Anthropic's percent-of-weekly-limit is a supplementary signal alongside the $
  // figure, not the only one (operator intent 2026-08-10) — read straight off the
  // already-fetched accounts registry (AccountView.weekly_pct), no new endpoint. Only
  // meaningful once a SPECIFIC account is selected (a percent has no cross-account
  // aggregate meaning), and only for Anthropic (the one subscription_boost_multiplier
  // provider with a weekly quota meter today).
  const selectedAccount =
    filter.provider === "anthropic" && accountFilter?.accountId !== undefined
      ? (accounts.find((a) => a.account_id === accountFilter.accountId) ?? null)
      : null;
  // Per-task cost RANKING (anthropic_per_task_actual_spend_and_account_calibration_
  // 2026_08_10 [BACKEND] P2). A separate fetch because the endpoint returns an
  // ORDERING rather than an aggregate — but the SAME filter set as the windows fetch
  // below, so a ranked task and the window totals can never disagree about which rows
  // they cover. Its own window selector because a ranking is only meaningful over a
  // stated period.
  const [rankingWindow, setRankingWindow] = useState("24h");
  const [ranking, setRanking] = useState<TopTaskCostView[]>([]);
  const [rankingError, setRankingError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getTopTasksByCost(
          baseUrl,
          windowLookbackHours(rankingWindow),
          25,
          filter.provider,
          filter.model,
          roleGroupFilter.roleGroup,
          accountFilter?.accountId,
          excludeDiedMidTask,
        );
        if (!cancelled) {
          setRanking(data);
          setRankingError(null);
        }
      } catch (e) {
        if (!cancelled) setRankingError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    baseUrl,
    rankingWindow,
    filter.provider,
    filter.model,
    roleGroupFilter.roleGroup,
    accountFilter?.accountId,
    excludeDiedMidTask,
  ]);

  // Registry fetch is separate from the usage poll and deliberately not retried on a
  // loop: accounts change on operator action, not continuously, and a failure here
  // must degrade to "All accounts" rather than blanking the panel — the usage numbers
  // are still correct without the filter row.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listAccounts(baseUrl);
        if (!cancelled) setAccounts(data);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  // The previously selected account may not belong to the newly selected provider —
  // reset to "All accounts" rather than pointing at a stale index into a
  // now-different-shaped options array.
  useEffect(() => {
    setAccountIndex(0);
  }, [filter.provider]);

  // Per-model death rates. Deliberately NOT scoped by the provider/model filter chips:
  // this IS the per-model breakdown, so slicing it to one model would leave a table of
  // one row and defeat the comparison. A failure degrades to an inline error, never a
  // blank panel — the usage numbers above are still correct without it.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getUsageDeathRates(baseUrl, deathRatesWindow);
        if (!cancelled) {
          setDeathRates(data);
          setDeathRatesError(null);
        }
      } catch (e) {
        if (!cancelled) setDeathRatesError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, deathRatesWindow]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getTaskUsageWindows(
          baseUrl,
          filter.provider,
          filter.model,
          roleGroupFilter.roleGroup,
          accountFilter?.accountId,
          excludeDiedMidTask,
        );
        if (!cancelled) {
          setWindows(data);
          setError(null);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, filter.provider, filter.model, roleGroupFilter.roleGroup, accountFilter?.accountId, excludeDiedMidTask]);

  // ao_billing_plans_provider_only_aggregate_2026_08_31 (AO billing/plans P0,
  // ci_unblock_chain_and_coverage_gaps_2026_08_30: "Aggregate per PROVIDER, not per
  // model — the Gem view should stop enumerating models"). Was: expand to one row
  // per MODEL once a specific provider was selected (cost_ranked_model_routing_
  // rebuild_2026_08_28). Now: this breakdown is PROVIDER-ONLY — one row per provider
  // aggregate under "All providers", and nothing further once a specific provider is
  // selected (the aggregate tables above already show that provider's own totals; a
  // per-model expansion here is exactly the automatic enumeration the operator asked
  // to stop). Per-model granularity is NOT deleted: PROVIDER_MODEL_FILTER_OPTIONS'
  // explicit deepseek-v4-pro/-flash buttons still let an operator deliberately
  // narrow to one model, and every historical task_usage row keeps its own model —
  // this only removes the AUTOMATIC secondary breakdown table.
  useEffect(() => {
    let cancelled = false;
    const loadBreakdown = async () => {
      if (filter.provider !== undefined) {
        // Already scoped to one provider (or, via the explicit buttons, one model
        // within it) — the aggregate tables above already cover it; no further
        // per-model breakdown is shown here.
        if (!cancelled) {
          setModelBreakdown([]);
          setModelBreakdownError(null);
        }
        return;
      }
      const slices = PROVIDER_MODEL_FILTER_OPTIONS.filter((o) => o.provider !== undefined && o.model === undefined);
      try {
        const results = await Promise.all(
          slices.map(async (s) => {
            const ws = await api.getTaskUsageWindows(
              baseUrl,
              s.provider,
              s.model,
              roleGroupFilter.roleGroup,
              accountFilter?.accountId,
              excludeDiedMidTask,
            );
            return { label: s.label, windows: ws };
          }),
        );
        if (!cancelled) {
          setModelBreakdown(results);
          setModelBreakdownError(null);
        }
      } catch (e) {
        if (!cancelled) setModelBreakdownError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadBreakdown();
    const id = setInterval(() => void loadBreakdown(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, filter.provider, roleGroupFilter.roleGroup, accountFilter?.accountId, excludeDiedMidTask]);

  // ao_v2_node_and_human_slot_recognition_2026_09_04 H6(display): resolve the
  // registered-host roster only while "Human" is selected — an unregistered
  // interactive session has no agent row to enumerate and stays folded into the
  // bare "human" aggregate table above, same as HumanFleet.tsx's own "still shows,
  // with fields blank" precedent for an unmatched slot.
  useEffect(() => {
    let cancelled = false;
    const loadHumanHostBreakdown = async () => {
      if (roleGroupFilter.roleGroup !== "human") {
        if (!cancelled) {
          setHumanHostBreakdown([]);
          setHumanHostBreakdownError(null);
        }
        return;
      }
      try {
        const [humanAgents, planningHumanAgents] = await Promise.all([
          api.listAgents(baseUrl, { kind: "human" }),
          api.listAgents(baseUrl, { kind: "planning-human" }),
        ]);
        const labels = humanHostLabels([...humanAgents, ...planningHumanAgents]);
        const results = await Promise.all(
          labels.map(async (label) => {
            const roleGroup = humanHostRoleGroup(label);
            const ws = await api.getTaskUsageWindows(
              baseUrl,
              filter.provider,
              filter.model,
              roleGroup,
              accountFilter?.accountId,
              excludeDiedMidTask,
            );
            return { label: roleGroup, windows: ws };
          }),
        );
        if (!cancelled) {
          setHumanHostBreakdown(results);
          setHumanHostBreakdownError(null);
        }
      } catch (e) {
        if (!cancelled) setHumanHostBreakdownError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadHumanHostBreakdown();
    const id = setInterval(() => void loadHumanHostBreakdown(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, roleGroupFilter.roleGroup, filter.provider, filter.model, accountFilter?.accountId, excludeDiedMidTask]);

  const sorted = sortWindows(windows);

  return (
    <Panel
      title="Task Token Usage"
      tone="accounts"
      right={
        <button className="btn btn-xs" onClick={() => setUsageTsOpen(true)}>
          Usage over time
        </button>
      }
    >
      {usageTsOpen && (
        <Suspense fallback={<div className="task-usage-pending">loading…</div>}>
          <UsageTimeSeriesModal
            baseUrl={baseUrl}
            open={usageTsOpen}
            onClose={() => setUsageTsOpen(false)}
            initialProvider={filter.provider}
            initialRoleGroup={roleGroupFilter.roleGroup}
          />
        </Suspense>
      )}
      <div className="usage-panel-hint">
        Completed tasks only, bucketed by completion time — a task still in progress contributes nothing here until it
        finishes. See the Accounts panel for real-time per-account totals instead.
      </div>
      <div className="task-usage-provider-toggle">
        {PROVIDER_MODEL_FILTER_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${filterIndex === i ? "active" : ""}`}
            onClick={() => setFilterIndex(i)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="task-usage-role-group-toggle">
        {ROLE_GROUP_FILTER_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${roleGroupIndex === i ? "active" : ""}`}
            onClick={() => setRoleGroupIndex(i)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="task-usage-died-mid-task-toggle">
        <button
          className={`btn btn-xs ${excludeDiedMidTask ? "active" : ""}`}
          title="Died-mid-task rows are real captured spend from a session that was killed before reaching /done — included by default. Toggle to exclude them from every total on this panel."
          onClick={() => setExcludeDiedMidTask((v) => !v)}
        >
          Hide died mid-task
        </button>
      </div>
      {accountOptions.length > 1 && (
        <div className="task-usage-account-toggle">
          {accountOptions.map((opt, i) => (
            <button
              key={opt.label}
              className={`btn btn-xs ${accountIndex === i ? "active" : ""}`}
              onClick={() => setAccountIndex(i)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {selectedAccount && (
        <div
          className="task-usage-weekly-pct"
          title="Anthropic's own quota-meter reading — a supplementary signal alongside the $ figure above, not the only one (the $ figure is list-priced value divided by this account's measured subscription multiplier, NOT this percent)."
        >
          Weekly limit: {selectedAccount.weekly_pct === null ? "—" : `${selectedAccount.weekly_pct}%`} used (
          {selectedAccount.label || selectedAccount.account_id})
        </div>
      )}
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !loaded && <div className="task-usage-pending">loading…</div>}
      {!error && loaded && sorted.length === 0 && (
        <div className="task-usage-pending">no completed-task usage recorded yet</div>
      )}
      {!error && sorted.length > 0 && (
        <table className="task-usage-window-table">
          <thead>
            <tr>
              <th></th>
              <th>Input</th>
              <th>Cache write</th>
              <th>Cache read</th>
              <th>Output</th>
              <th>Total</th>
              <th>Spend</th>
              <th>Tasks</th>
              <th>Avg tok/task</th>
              <th>Avg $/task</th>
              {/* Reasoning is APPENDED last, not inserted after Output — this table's
                  column positions are asserted by nth() index in task-usage-account-
                  filter.spec.ts / task-usage-provider-filter.spec.ts / task-usage-role-
                  group-filter.spec.ts; appending at the end is the only placement that
                  doesn't shift every column after it out from under those specs. */}
              <th title="DeepSeek native-proxy reasoning-token breakdown — a subset WITHIN Output, not additive. — for a window with no DeepSeek native capture (non-DeepSeek provider, or predates the native proxy)">
                Reasoning
              </th>
              {/* provider_concurrency_backoff_alerting_parity_2026_08_27: derived decode
                  throughput — output tokens ÷ wall-clock seconds (TaskUsageRow's own
                  [assigned_at, completed_at] window), directly comparable to Z.ai's
                  public status-page "Peak avg Decode" figures (observed 2026-08-21..27:
                  Max&Pro ~85-120 t/s, Lite ~65-95 t/s; per-instance vs account-shared
                  unknown — cited for reference only, NOT a cap threshold, no decision
                  in this todo). Appended after Reasoning for the same nth()-stability
                  reason above (Reasoning stays at its prior nth, this new column is
                  strictly additive at the end). */}
              <th title="Output tokens per second — output_tokens ÷ wall-clock seconds (TaskUsageRow [assigned_at, completed_at]), directly comparable to Z.ai's public 'Peak avg Decode' (Max&Pro ~85-120 t/s, Lite ~65-95 t/s 2026-08-21..27; per-instance vs shared unknown — reference only, not a cap threshold)">
                Out tok/s
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <tr key={w.window}>
                <td className="task-usage-window-label">{windowLabel(w.window)}</td>
                <td className="mono">{fmtTokenCount(w.input_tokens)}</td>
                <td className="mono">{fmtTokenCount(w.cache_creation_input_tokens)}</td>
                <td className="mono">{fmtTokenCount(w.cache_read_input_tokens)}</td>
                <td className="mono">{fmtTokenCount(w.output_tokens)}</td>
                <td className="mono">{fmtTokenCount(totalTokens(w))}</td>
                <td
                  className="mono"
                  title={
                    spendLowerBoundText(w.spend_usd, w.priced_spend_usd, w.unpriced_row_count, w.unmeasured_row_count)
                      ? `Lower bound: the priced rows' sum. ${w.unmeasured_row_count ?? 0} unmeasured row(s) (node ` +
                        `tombstones with no transcript) and ${w.unpriced_row_count} unpriced row(s) are not in it.`
                      : (spendCellTitle(w.spend_usd, w.unpriced_row_count) ??
                        estimatedCellTitle(w.spend_usd, w.estimated_row_count))
                  }
                >
                  {spendLowerBoundText(w.spend_usd, w.priced_spend_usd, w.unpriced_row_count, w.unmeasured_row_count) ??
                    fmtUsdOrDashWithEstimateFlag(w.spend_usd, w.estimated_row_count)}
                  <SpendBasisBadge basis={w.spend_basis} />
                </td>
                <td className="mono">{w.task_count}</td>
                <td className="mono">{w.avg_tokens_per_task === null ? "—" : fmtTokenCount(w.avg_tokens_per_task)}</td>
                <td
                  className="mono"
                  title={
                    perUnitLowerBoundText(w.avg_spend_per_task, w.priced_spend_usd, w.unpriced_row_count, w.task_count)
                      ? perUnitLowerBoundTitle("task", w.unpriced_row_count)
                      : (spendCellTitle(w.avg_spend_per_task, w.unpriced_row_count) ??
                        estimatedCellTitle(w.avg_spend_per_task, w.estimated_row_count))
                  }
                >
                  {perUnitLowerBoundText(
                    w.avg_spend_per_task,
                    w.priced_spend_usd,
                    w.unpriced_row_count,
                    w.task_count,
                  ) ?? fmtUsdOrDashWithEstimateFlag(w.avg_spend_per_task, w.estimated_row_count)}
                </td>
                <td className="mono">{fmtTokenCountOrDash(w.reasoning_tokens)}</td>
                <td
                  className="mono"
                  title="Output tokens per second — directly comparable to Z.ai's Peak avg Decode (reference only, not a cap)"
                >
                  {fmtTokensPerSecOrDash(w.output_tokens_per_second)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!error && sorted.length > 0 && (
        <table className="task-usage-window-table task-usage-per-turn-table">
          <thead>
            <tr>
              <th title="Per-turn efficiency — how a task's turns break down, not just its total">Per turn</th>
              <th>Turns</th>
              <th>Turns/task</th>
              <th title="Input, cache miss">In/turn</th>
              <th title="Cache write (billed at the input-miss rate)">CacheWr/turn</th>
              <th title="Cache read (input-hit rate)">CacheRd/turn</th>
              <th>Out/turn</th>
              <th>$/turn</th>
              {/* Appended last for the same nth()-index-stability reason as the totals
                  table above (deepseek-per-turn-metrics.spec.ts asserts this table's
                  columns by position). */}
              <th title="DeepSeek native-proxy reasoning-token breakdown per turn — a subset WITHIN Out/turn, not additive">
                Reasoning/turn
              </th>
              {/* provider_concurrency_backoff_alerting_parity_2026_08_27: same derived
                  throughput as the totals table, appended as last column for the same
                  nth()-stability reason — Reasoning/turn stays at its prior nth(8),
                  this new column is strictly additive. Title cites Z.ai Peak avg
                  Decode comparability (~85-120 Max&Pro, ~65-95 Lite) for reference
                  only, not a cap. */}
              <th title="Output tokens per second — output_tokens ÷ wall-clock seconds (TaskUsageRow [assigned_at, completed_at]), directly comparable to Z.ai's public 'Peak avg Decode' (Max&Pro ~85-120 t/s, Lite ~65-95 t/s 2026-08-21..27; reference only, not a cap threshold)">
                Out tok/s
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <tr key={w.window}>
                <td className="task-usage-window-label">{windowLabel(w.window)}</td>
                <td className="mono">{w.turn_count}</td>
                <td className="mono">{fmtRatioOrDash(w.avg_turns_per_task)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.input_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.cache_creation_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.cache_read_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.output_tokens_per_turn)}</td>
                <td
                  className="mono"
                  title={
                    perUnitLowerBoundText(w.spend_per_turn, w.priced_spend_usd, w.unpriced_row_count, w.turn_count)
                      ? perUnitLowerBoundTitle("turn", w.unpriced_row_count)
                      : (spendCellTitle(w.spend_per_turn, w.unpriced_row_count) ??
                        estimatedCellTitle(w.spend_per_turn, w.estimated_row_count))
                  }
                >
                  {perUnitLowerBoundText(w.spend_per_turn, w.priced_spend_usd, w.unpriced_row_count, w.turn_count) ??
                    fmtUsdOrDashWithEstimateFlag(w.spend_per_turn, w.estimated_row_count)}
                </td>
                <td className="mono">{fmtTokenAvgOrDash(w.reasoning_tokens_per_turn)}</td>
                <td
                  className="mono"
                  title="Output tokens per second — directly comparable to Z.ai's Peak avg Decode (reference only, not a cap)"
                >
                  {fmtTokensPerSecOrDash(w.output_tokens_per_second)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* ao_billing_plans_provider_only_aggregate_2026_08_31: PROVIDER-only breakdown
          (never per-model — see the loadBreakdown effect above for why). Multipliers
          stay (provider, plan, date-window) and are never shown here; this is purely
          token/spend/turn visibility per provider, additive to the aggregate tables
          above so Playwright nth-position assertions stay green. Only ever populated
          when "All providers" is selected (see loadBreakdown), so the heading and
          column no longer need to branch on filter.provider. */}
      {!error && loaded && modelBreakdown.length > 0 && (
        <div data-testid="task-usage-model-breakdown">
          <div className="usage-panel-hint">
            Breakdown by provider (30d token/spend) — multipliers stay per (provider, plan, date-window), not per-model.
          </div>
          {modelBreakdownError && (
            <div className="task-usage-error">Failed to load model breakdown: {modelBreakdownError}</div>
          )}
          <table className="task-usage-window-table task-usage-model-breakdown-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Input</th>
                <th>Cache write</th>
                <th>Cache read</th>
                <th>Output</th>
                <th>Total</th>
                <th>Spend</th>
                <th>Tasks</th>
                <th>Out tok/s</th>
              </tr>
            </thead>
            <tbody>
              {modelBreakdown.map((slice) => {
                const w30 =
                  [...slice.windows].find((w) => w.window === "30d") ??
                  [...slice.windows].find((w) => w.window === "lifetime");
                if (!w30) return null;
                return (
                  <tr key={slice.label}>
                    <td className="task-usage-window-label">{slice.label}</td>
                    <td className="mono">{fmtTokenCount(w30.input_tokens)}</td>
                    <td className="mono">{fmtTokenCount(w30.cache_creation_input_tokens)}</td>
                    <td className="mono">{fmtTokenCount(w30.cache_read_input_tokens)}</td>
                    <td className="mono">{fmtTokenCount(w30.output_tokens)}</td>
                    <td className="mono">{fmtTokenCount(totalTokens(w30))}</td>
                    <td
                      className="mono"
                      title={
                        spendLowerBoundText(
                          w30.spend_usd,
                          w30.priced_spend_usd,
                          w30.unpriced_row_count,
                          w30.unmeasured_row_count,
                        )
                          ? `Lower bound: the priced rows' sum. ${w30.unmeasured_row_count ?? 0} unmeasured and ${w30.unpriced_row_count} unpriced row(s) are not in it.`
                          : (spendCellTitle(w30.spend_usd, w30.unpriced_row_count) ??
                            estimatedCellTitle(w30.spend_usd, w30.estimated_row_count))
                      }
                    >
                      {spendLowerBoundText(
                        w30.spend_usd,
                        w30.priced_spend_usd,
                        w30.unpriced_row_count,
                        w30.unmeasured_row_count,
                      ) ?? fmtUsdOrDashWithEstimateFlag(w30.spend_usd, w30.estimated_row_count)}
                    </td>
                    <td className="mono">{w30.task_count}</td>
                    <td className="mono">{fmtTokensPerSecOrDash(w30.output_tokens_per_second)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Per-model death rates (ao_death_reasons_gate_starvation_and_cost_columns_
          findings_2026_10_02 [BACKEND] P2). Two rates, two denominators, never summed:
          the flagged rate is per ROW (the UI's historical basis) and the activity-log
          rate is per TASK (a death is a per-task event). A large gap between them is
          the point — it means deaths are happening that the transcript flag never
          attributed. */}
      {deathRates && (
        <div data-testid="task-usage-death-rates">
          <div className="usage-panel-hint">
            Death rate per model — the transcript <code>died_mid_task</code> flag beside the activity log&apos;s
            work-losing slot deaths. Different denominators (rows vs tasks) by construction; the gap is the signal.
          </div>
          <label className="task-usage-death-window">
            Window:{" "}
            <select value={deathRatesWindow} onChange={(e) => setDeathRatesWindow(e.target.value)}>
              {["1h", "5h", "24h", "7d", "30d"].map((w) => (
                <option key={w} value={w}>
                  {_WINDOW_LABELS[w] ?? w}
                </option>
              ))}
            </select>
          </label>
          {deathRatesError && <div className="task-usage-error">Failed to load death rates: {deathRatesError}</div>}
          <table className="task-usage-window-table task-usage-death-rates-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Provider</th>
                <th>Flagged (died_mid_task)</th>
                <th>Activity log (per 100 tasks)</th>
                <th>Tasks</th>
              </tr>
            </thead>
            <tbody>
              {deathRates.models.map((m) => (
                <tr key={m.model}>
                  <td className="task-usage-window-label mono">{m.model}</td>
                  <td className="mono">{m.provider ?? "—"}</td>
                  <td
                    className="mono"
                    title={`${m.flagged_death_rows} of ${m.row_count} attempt row(s) carry died_mid_task=True. The flag is only set when a transcript was recoverable at death time, so it undercounts.`}
                  >
                    {fmtPer100(m.flagged_per_100, m.row_count)}% ({m.flagged_death_rows}/{m.row_count})
                  </td>
                  <td
                    className="mono"
                    title={`${m.activity_death_count} non-intentional slot-scope tmux_session_lost event(s) over ${m.task_count} task(s). Measured from the activity log, not a transcript — this is the number the flag misses.`}
                  >
                    {fmtPer100(m.activity_per_100, m.task_count)} ({m.activity_death_count}/{m.task_count})
                  </td>
                  <td className="mono">{m.task_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {deathRates.unattributed_activity_deaths > 0 && (
            <div className="usage-panel-hint" data-testid="task-usage-death-rates-unattributed">
              + {deathRates.unattributed_activity_deaths} work-losing slot death(s) whose event stamped no model (mostly
              node kills) are not in any row above.
            </div>
          )}
        </div>
      )}
      {/* ao_v2_node_and_human_slot_recognition_2026_09_04 H6(display): per-host
          breakdown, one row per registered human/planning-human laptop — only
          rendered while the "Human" role-group filter is active, since a host
          breakdown is meaningless under any other role group. */}
      {!error && loaded && roleGroupFilter.roleGroup === "human" && (
        <div data-testid="task-usage-human-host-breakdown">
          <div className="usage-panel-hint">
            Breakdown by host (30d) — one row per registered human laptop with usage attributed to it.
          </div>
          {humanHostBreakdownError && (
            <div className="task-usage-error">Failed to load host breakdown: {humanHostBreakdownError}</div>
          )}
          {!humanHostBreakdownError && humanHostBreakdown.length === 0 && (
            <div className="usage-panel-hint">No registered human hosts yet — see the Human Fleet page.</div>
          )}
          {humanHostBreakdown.length > 0 && (
            <table className="task-usage-window-table task-usage-human-host-breakdown-table">
              <thead>
                <tr>
                  <th>Host</th>
                  <th>Input</th>
                  <th>Cache write</th>
                  <th>Cache read</th>
                  <th>Output</th>
                  <th>Total</th>
                  <th>Spend</th>
                  <th>Tasks</th>
                  <th>Out tok/s</th>
                </tr>
              </thead>
              <tbody>
                {humanHostBreakdown.map((slice) => {
                  const w30 =
                    [...slice.windows].find((w) => w.window === "30d") ??
                    [...slice.windows].find((w) => w.window === "lifetime");
                  if (!w30) return null;
                  return (
                    <tr key={slice.label}>
                      <td className="task-usage-window-label">{slice.label}</td>
                      <td className="mono">{fmtTokenCount(w30.input_tokens)}</td>
                      <td className="mono">{fmtTokenCount(w30.cache_creation_input_tokens)}</td>
                      <td className="mono">{fmtTokenCount(w30.cache_read_input_tokens)}</td>
                      <td className="mono">{fmtTokenCount(w30.output_tokens)}</td>
                      <td className="mono">{fmtTokenCount(totalTokens(w30))}</td>
                      <td
                        className="mono"
                        title={
                          spendLowerBoundText(
                            w30.spend_usd,
                            w30.priced_spend_usd,
                            w30.unpriced_row_count,
                            w30.unmeasured_row_count,
                          )
                            ? `Lower bound: the priced rows' sum. ${w30.unmeasured_row_count ?? 0} unmeasured and ${w30.unpriced_row_count} unpriced row(s) are not in it.`
                            : (spendCellTitle(w30.spend_usd, w30.unpriced_row_count) ??
                              estimatedCellTitle(w30.spend_usd, w30.estimated_row_count))
                        }
                      >
                        {spendLowerBoundText(
                          w30.spend_usd,
                          w30.priced_spend_usd,
                          w30.unpriced_row_count,
                          w30.unmeasured_row_count,
                        ) ?? fmtUsdOrDashWithEstimateFlag(w30.spend_usd, w30.estimated_row_count)}
                      </td>
                      <td className="mono">{w30.task_count}</td>
                      <td className="mono">{fmtTokensPerSecOrDash(w30.output_tokens_per_second)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <h4 style={{ margin: 0 }}>Top tasks by cost</h4>
          <span className="muted" style={{ fontSize: 12 }}>
            Ranked by LIST-priced value, highest first. A flat-rate plan&apos;s measured value multiplier divides every
            task in the window by the same constant, so the ORDER holds even before the subscription calibration settles
            — a leading <span className="mono">~</span> marks a dollar that is not yet real cash (hover for why).
          </span>
          <select
            aria-label="Cost ranking window"
            value={rankingWindow}
            onChange={(e) => setRankingWindow(e.target.value)}
          >
            {_WINDOW_ORDER.map((w) => (
              <option key={w} value={w}>
                {windowLabel(w)}
              </option>
            ))}
          </select>
        </div>
        {rankingError ? (
          <p className="muted">Cost ranking unavailable: {rankingError}</p>
        ) : ranking.length === 0 ? (
          <p className="muted">No completed tasks with usage in this window.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="task-cost-ranking-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Task</th>
                  <th>Provider / model</th>
                  <th>List value</th>
                  <th>Basis</th>
                  <th>Turns</th>
                  <th>Tokens</th>
                  <th>Attempts</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((row, i) => (
                  <tr key={row.task_id}>
                    <td className="mono">{i + 1}</td>
                    <td className="mono" title={row.task_id}>
                      {row.task_id}
                    </td>
                    <td>
                      {row.provider ?? "unattributed"} / {row.model ?? "—"}
                    </td>
                    <td className="mono" title={costRankingCellTitle(row)}>
                      {row.list_value_usd === null
                        ? "—"
                        : `${row.absolute_is_provisional ? "~" : ""}${fmtUsdOrDash(row.list_value_usd)}`}
                    </td>
                    <td>{spendBasisLabel(row.spend_basis)}</td>
                    <td className="mono">{row.turn_count}</td>
                    <td className="mono">
                      {fmtTokenCount(
                        row.input_tokens +
                          row.cache_creation_input_tokens +
                          row.cache_read_input_tokens +
                          row.output_tokens,
                      )}
                    </td>
                    <td className="mono">{row.attempt_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
